
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ReactNode } from "react";

interface ValueBenefitProps {
  title: string;
  description: string;
}

const ValueBenefit = ({ title, description }: ValueBenefitProps) => (
  <div className="flex items-start gap-3">
    <CheckCircle2 className="h-5 w-5 text-edgefi-purple mt-0.5 flex-shrink-0" />
    <div>
      <p className="text-gray-800 font-medium">{title}</p>
      <p className="text-gray-600 text-sm mt-1">{description}</p>
    </div>
  </div>
);

interface PlatformValueProps {
  title: string;
  subtitle: string;
  description: string;
  benefits: ValueBenefitProps[];
  imageSrc: string;
  imageAlt: string;
  linkUrl: string;
  linkText: string;
  customImageComponent?: ReactNode;
}

export const PlatformValueSection = ({
  title,
  subtitle,
  description,
  benefits,
  imageSrc,
  imageAlt,
  linkUrl,
  linkText,
  customImageComponent,
}: PlatformValueProps) => {
  return (
    <section className="py-16 bg-gradient-to-r from-white to-purple-50/20">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-5">
                {subtitle}
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">{title}</h2>
              <p className="text-lg text-gray-600 mb-8">{description}</p>

              <div className="space-y-6 mb-8">
                {benefits.map((benefit, index) => (
                  <ValueBenefit key={index} title={benefit.title} description={benefit.description} />
                ))}
              </div>

              <Link to={linkUrl} className="inline-flex">
                <Button className="bg-edgefi-purple hover:bg-edgefi-purple/90 text-white group">
                  {linkText}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              {customImageComponent ? (
                customImageComponent
              ) : (
                <div className="relative overflow-hidden rounded-2xl shadow-xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-500/10 mix-blend-multiply" />
                  <img
                    src={imageSrc}
                    alt={imageAlt}
                    className="w-full h-auto object-cover"
                  />
                </div>
              )}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-chartreuse-500/20 rounded-full blur-3xl" />
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-edgefi-purple/20 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
